// src/components/dashboard/WhatYouveDone.jsx
//
// A plain record of what you've done: the modules you have finished (and
// when, where that was recorded), whether you have taken the Vibe Check, and
// how many insights you have saved. A list, not a score: no percentages, no
// rings, no levels, and nothing about what you haven't done.
import React from 'react';
import { CheckCircle2, ClipboardList, Lightbulb, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../shared/CardComponents';
import { friendshipModules } from '../../data/modules/friendship-modules-index';

const formatDate = (iso) => {
  const when = Date.parse(iso || '');
  if (!when) return null;
  return new Date(when).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
};

const LinkButton = ({ onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1 text-sm text-teal-300 hover:text-white transition-colors"
  >
    {children}
    <ArrowRight className="w-3 h-3" aria-hidden="true" />
  </button>
);

const WhatYouveDone = ({
  completedModules = [],
  moduleCompletedAt = {},
  savedInsightsCount = 0,
  assessmentResults = null,
  setCurrentView,
  onShowInsights,
}) => {
  const finished = friendshipModules
    .filter((m) => completedModules.includes(m.id))
    .sort((a, b) => a.id - b.id);
  const tookVibeCheck = !!assessmentResults && Object.keys(assessmentResults).length > 0;
  const vibeCheckDate = formatDate(assessmentResults?.completedAt);

  return (
    <div className="space-y-6" data-testid="what-youve-done">
      <Card className="bg-white/5 backdrop-blur-sm border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-white text-lg">
            <CheckCircle2 className="w-5 h-5 text-teal-400" aria-hidden="true" />
            Modules you&apos;ve finished
          </CardTitle>
        </CardHeader>
        <CardContent>
          {finished.length === 0 ? (
            <p className="text-slate-300">
              None yet. A module counts as finished once you&apos;ve marked each of its parts done.
            </p>
          ) : (
            <ul className="divide-y divide-white/10">
              {finished.map((module) => {
                const date = formatDate(moduleCompletedAt?.[module.id]);
                return (
                  <li key={module.id} className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                    <span className="text-white">
                      <span className="text-slate-400">Module {module.id}:</span> {module.title}
                    </span>
                    {date && <span className="text-xs text-slate-400">Finished {date}</span>}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="bg-white/5 backdrop-blur-sm border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-white text-lg">
              <ClipboardList className="w-5 h-5 text-purple-400" aria-hidden="true" />
              Friendship Vibe Check
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-slate-300">
              {tookVibeCheck
                ? (vibeCheckDate ? `You took it on ${vibeCheckDate}.` : 'You have taken it.')
                : 'Not taken yet.'}
            </p>
            {typeof setCurrentView === 'function' && (
              <LinkButton onClick={() => setCurrentView(tookVibeCheck ? 'vibe-assessment-results' : 'vibe-assessment-intro')}>
                {tookVibeCheck ? 'See your results' : 'Take the Vibe Check'}
              </LinkButton>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white/5 backdrop-blur-sm border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-white text-lg">
              <Lightbulb className="w-5 h-5 text-yellow-400" aria-hidden="true" />
              Saved insights
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-slate-300">
              {savedInsightsCount === 0
                ? 'None saved yet.'
                : `${savedInsightsCount} saved.`}
            </p>
            {savedInsightsCount > 0 && typeof onShowInsights === 'function' && (
              <LinkButton onClick={onShowInsights}>Read them</LinkButton>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WhatYouveDone;
